import { useMemo } from 'react';
import type { FC } from 'react';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CalculateIcon from '@mui/icons-material/Calculate';
import EventIcon from '@mui/icons-material/Event';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import HubIcon from '@mui/icons-material/Hub';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

import { ColorEnum } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import type {
  AccountingStateId,
  Id,
  MissionStateId,
  QuotationStateId,
  ReportStateId,
} from '@e-socna/data-layer';

import {
  PATH_MISSION_ACCOUNTING_DEFAULT,
  PATH_MISSION_BUSINESS_EVENTS,
  PATH_MISSION_DEFAULT,
  PATH_MISSION_FILES_DEFAULT,
  PATH_MISSION_QUOTATION_DEFAULT,
  PATH_MISSION_REPORTS,
  PATH_MISSION_SERVICE_USAGES_DEFAULT,
  PATH_MISSION_SUB_MISSIONS,
  PATH_MISSION_TOOLKITS_DEFAULT,
  PATH_NOT_FOUND,
} from '../../../app/constants/paths.constant';
import { MissionSpaceEnum } from '../../../app/enums/mission-space.enum';
import {
  MISSION_SPACE_PARAM,
  missionSpacePathOf,
} from '../../../app/utils/mission-space.util';
import type { MissionSpacePath } from '../../../app/utils/mission-space.util';
import { hasSubMissions } from '../../../app/utils/mission-kind-profile.util';
import { Avatar } from '../../../components/Avatar';
import { Page } from '../../../contexts/NavigationContext/enums/page.enum';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { iconToMuiIcon } from '../../../utils/icon-to-mui-icon.util';
import { formatAmount } from '../../../utils/format-amount.util';
import type {
  MissionSpaceCounts,
  MissionSpaceInfo,
} from '../../../contexts/NavigationContext/types/spaces.type';
import type { useColorToMuiColor } from '../../../hooks/useColorToMuiColor.hook';

import { Space } from '../enums/space.enum';
import type { MenuLink, MenuSpace } from '../types/menu.type';

type ColorToMuiColor = ReturnType<typeof useColorToMuiColor>['colorToMuiColor'];

type ChipState = Readonly<{
  label: string;
  chip: Readonly<{
    color: ColorEnum;
    icon: Parameters<typeof iconToMuiIcon>[0];
  }>;
}>;

type MissionSpacePageIds = Readonly<{
  general: Page;
  serviceUsages: Page;
  quotation: Page;
  subMissions: Page;
  toolkits: Page;
  reports: Page;
  accounting: Page;
  businessEvents: Page;
  files: Page;
}>;

// The pages of the current mission, found in the menu to highlight them.
const CURRENT_MISSION_PAGE_IDS: MissionSpacePageIds = {
  general: Page.Mission,
  serviceUsages: Page.MissionServiceUsages,
  quotation: Page.MissionQuotation,
  subMissions: Page.MissionSubMissions,
  toolkits: Page.MissionToolkits,
  reports: Page.MissionReport,
  accounting: Page.MissionAccounting,
  businessEvents: Page.MissionBusinessEvents,
  files: Page.MissionFiles,
};

const RELATED_MISSION_PAGE_IDS: MissionSpacePageIds = {
  general: Page.RelatedMission,
  serviceUsages: Page.RelatedMissionServiceUsages,
  quotation: Page.RelatedMissionQuotation,
  subMissions: Page.RelatedMissionSubMissions,
  toolkits: Page.RelatedMissionToolkits,
  reports: Page.RelatedMissionReport,
  accounting: Page.RelatedMissionAccounting,
  businessEvents: Page.RelatedMissionBusinessEvents,
  files: Page.RelatedMissionFiles,
};

type MissionSpaceTarget = Readonly<{
  pageIds: MissionSpacePageIds;
  folderId: Id;
  missionId: Id;
  missionSpace: MissionSpacePath;
  missionStateId: MissionStateId;
  quotationStateId: QuotationStateId;
  accountingStateId: AccountingStateId;
  reportStateId?: ReportStateId;
  // The counts are only known for the current mission.
  counts?: MissionSpaceCounts;
}>;

type FirstPage = Readonly<{ title: string; Icon: FC }>;

const MISSION_FIRST_PAGE: FirstPage = {
  title: 'Mission',
  Icon: BusinessCenterIcon,
};
const COLLABORATION_FIRST_PAGE: FirstPage = {
  title: 'Collaboration',
  Icon: HubIcon,
};
const SUB_MISSION_FIRST_PAGE: FirstPage = {
  title: 'Sous-Mission',
  Icon: BusinessCenterIcon,
};

const MISSION_SPACE_SIBLINGS = [
  Space.Main,
  Space.Folder,
  Space.Mission,
  Space.SubMission,
  Space.MasterData,
  Space.References,
  Space.Admin,
];

export const useMissionMenuSpaces = ({
  missionSpaceInfo,
  hasAccounting,
  colorToMuiColor,
}: Readonly<{
  missionSpaceInfo: MissionSpaceInfo | undefined;
  // Whether the current mission has an accounting (a sub-mission billed by
  // its collaboration has none).
  hasAccounting: boolean;
  colorToMuiColor: ColorToMuiColor;
}>): Readonly<{ mission: MenuSpace; subMission: MenuSpace }> => {
  const {
    accountingStatesIdMap,
    currenciesIdMap,
    missionStatesIdMap,
    quotationStatesIdMap,
    reportStatesIdMap,
  } = useReferencesContext();

  return useMemo(() => {
    const stateAvatar = (state: ChipState | undefined) =>
      state ? (
        <Avatar
          bgColor={colorToMuiColor(state.chip.color)}
          icon={iconToMuiIcon(state.chip.icon)}
          label={`État : ${state.label}`}
        />
      ) : undefined;

    const countAvatar = (count: number | undefined, label: string) =>
      count === undefined ? undefined : (
        <Avatar
          bgColor={colorToMuiColor(
            count ? ColorEnum.Primary : ColorEnum.Default,
          )}
          label={label}
          shortName={count.toFixed()}
        />
      );

    const buildPages = ({
      target,
      pageIds,
      firstPage,
      isCollaboration,
      isAccountingAvailable,
    }: Readonly<{
      target: MissionSpaceTarget | undefined;
      pageIds: MissionSpacePageIds;
      firstPage: FirstPage;
      isCollaboration: boolean;
      isAccountingAvailable: boolean;
    }>): readonly MenuLink[] => {
      const link = (path: string) =>
        target
          ? buildPath(path, {
              folderId: target.folderId,
              missionId: target.missionId,
              [MISSION_SPACE_PARAM]: target.missionSpace,
            })
          : PATH_NOT_FOUND;
      const counts = target?.counts;
      const reportState = target?.reportStateId
        ? reportStatesIdMap[target.reportStateId]
        : undefined;

      return [
        {
          id: pageIds.general,
          title: firstPage.title,
          Icon: firstPage.Icon,
          to: link(PATH_MISSION_DEFAULT),
          secondaryAction: stateAvatar(
            target && missionStatesIdMap[target.missionStateId],
          ),
        },
        {
          id: pageIds.serviceUsages,
          title: 'Chiffrage',
          Icon: CalculateIcon,
          to: link(PATH_MISSION_SERVICE_USAGES_DEFAULT),
          secondaryAction: counts?.pricingTotalExcludingVat ? (
            <Tooltip title="Montant total HT">
              <Chip
                label={formatAmount(
                  counts.pricingTotalExcludingVat,
                  currenciesIdMap,
                )}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          ) : undefined,
        },
        {
          id: pageIds.quotation,
          title: 'Devis',
          Icon: ReceiptLongIcon,
          to: link(PATH_MISSION_QUOTATION_DEFAULT),
          secondaryAction: stateAvatar(
            target && quotationStatesIdMap[target.quotationStateId],
          ),
        },
        ...(isCollaboration
          ? [
              {
                id: pageIds.subMissions,
                title: 'Sous-Missions',
                Icon: SubdirectoryArrowRightIcon,
                to: link(PATH_MISSION_SUB_MISSIONS),
                secondaryAction: countAvatar(
                  counts?.subMissionsCount,
                  'Nombre de Sous-Missions',
                ),
              },
            ]
          : [
              {
                id: pageIds.toolkits,
                title: 'Études et Outils',
                Icon: ArchitectureIcon,
                to: link(PATH_MISSION_TOOLKITS_DEFAULT),
                secondaryAction: countAvatar(
                  counts?.toolkitsCount,
                  "Nombre d'Ensembles d'Outils",
                ),
              },
              {
                id: pageIds.reports,
                title: 'Rapports',
                Icon: HistoryEduIcon,
                to: link(PATH_MISSION_REPORTS),
                secondaryAction:
                  stateAvatar(reportState) ??
                  countAvatar(counts?.reportsCount, 'Nombre de Rapports'),
              },
            ]),
        {
          id: pageIds.accounting,
          title: 'Comptabilité',
          Icon: AccountBalanceIcon,
          to: link(PATH_MISSION_ACCOUNTING_DEFAULT),
          disabled: !isAccountingAvailable,
          secondaryAction: isAccountingAvailable
            ? stateAvatar(
                target && accountingStatesIdMap[target.accountingStateId],
              )
            : undefined,
        },
        {
          id: pageIds.businessEvents,
          title: 'Événements Métier',
          Icon: EventIcon,
          to: link(PATH_MISSION_BUSINESS_EVENTS),
          secondaryAction: countAvatar(
            counts?.businessEventsCount,
            "Nombre d'Événements Métier",
          ),
        },
        {
          id: pageIds.files,
          title: 'Fichiers',
          Icon: AttachFileIcon,
          to: link(PATH_MISSION_FILES_DEFAULT),
          secondaryAction: countAvatar(
            counts?.filesCount,
            'Nombre de Fichiers',
          ),
        },
      ];
    };

    const currentTarget: MissionSpaceTarget | undefined = missionSpaceInfo && {
      pageIds: CURRENT_MISSION_PAGE_IDS,
      folderId: missionSpaceInfo.folderId,
      missionId: missionSpaceInfo.missionId,
      missionSpace: missionSpacePathOf({
        kind: missionSpaceInfo.kind,
        compositeMissionId: missionSpaceInfo.compositeMission?.missionId,
      }),
      missionStateId: missionSpaceInfo.missionStateId,
      quotationStateId: missionSpaceInfo.quotationStateId,
      accountingStateId: missionSpaceInfo.accountingStateId,
      reportStateId: missionSpaceInfo.reportStateId,
      counts: missionSpaceInfo,
    };

    const isComposite =
      !!missionSpaceInfo && hasSubMissions(missionSpaceInfo.kind);
    const parentCollaboration = missionSpaceInfo?.compositeMission;
    const isCollaboration = isComposite || !!parentCollaboration;

    // A sub-mission shows its collaboration in the mission space, and itself in
    // the sub-mission space.
    const missionSpaceTarget: MissionSpaceTarget | undefined =
      missionSpaceInfo && parentCollaboration
        ? {
            pageIds: RELATED_MISSION_PAGE_IDS,
            folderId: missionSpaceInfo.folderId,
            missionId: parentCollaboration.missionId,
            missionSpace: MissionSpaceEnum.Collaborations,
            missionStateId: parentCollaboration.missionStateId,
            quotationStateId: parentCollaboration.quotationStateId,
            accountingStateId: parentCollaboration.accountingStateId,
            counts: parentCollaboration,
          }
        : currentTarget;
    const subMissionSpaceTarget = parentCollaboration
      ? currentTarget
      : undefined;

    return {
      mission: {
        title: isCollaboration ? 'Espace Collaboration' : 'Espace Mission',
        disabled: !missionSpaceInfo,
        enabledSiblings: MISSION_SPACE_SIBLINGS,
        flushFolderSpaceInfoOnEnter: false,
        flushMissionSpaceInfoOnEnter: false,
        pages: buildPages({
          target: missionSpaceTarget,
          pageIds: missionSpaceTarget?.pageIds ?? CURRENT_MISSION_PAGE_IDS,
          firstPage: isCollaboration
            ? COLLABORATION_FIRST_PAGE
            : MISSION_FIRST_PAGE,
          isCollaboration,
          // A collaboration always has an accounting.
          isAccountingAvailable: isCollaboration || hasAccounting,
        }),
      },
      subMission: {
        title: 'Espace Sous-Mission',
        hidden: !isCollaboration,
        disabled: !subMissionSpaceTarget,
        enabledSiblings: MISSION_SPACE_SIBLINGS,
        flushFolderSpaceInfoOnEnter: false,
        flushMissionSpaceInfoOnEnter: false,
        pages: buildPages({
          target: subMissionSpaceTarget,
          pageIds: subMissionSpaceTarget?.pageIds ?? RELATED_MISSION_PAGE_IDS,
          firstPage: SUB_MISSION_FIRST_PAGE,
          isCollaboration: false,
          isAccountingAvailable: !!subMissionSpaceTarget && hasAccounting,
        }),
      },
    };
  }, [
    accountingStatesIdMap,
    colorToMuiColor,
    currenciesIdMap,
    hasAccounting,
    missionSpaceInfo,
    missionStatesIdMap,
    quotationStatesIdMap,
    reportStatesIdMap,
  ]);
};
